import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';

/**
 * Band two: how things stand, as distinct from what is wrong.
 *
 * One rule shapes this whole band. Of every endpoint the platform exposes, only
 * consumption returns a dated series; the rest return a distribution or a
 * scalar at a point in time. So consumption gets a chart with a time axis and
 * nothing else does — a line drawn through four status counts would imply a
 * history that was never recorded, which is the fault this codebase has already
 * had to correct twice.
 *
 * The distributions are drawn as segmented bars rather than pie charts or
 * recharts bars: four buckets in a row read faster than a legend, they theme
 * from the same tokens as the rest of the app, and a bucket with nothing in it
 * simply takes no width instead of drawing an empty axis.
 */

type Segment = { label: string; value: number; className: string };

/** A distribution as one bar. Empty buckets take no width and no legend entry. */
function SegmentedBar({
  title,
  caption,
  segments,
  empty,
}: {
  title: string;
  caption?: string;
  segments: Segment[];
  empty: string;
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const present = segments.filter((s) => s.value > 0);

  return (
    <div className={cn(PANEL, 'p-5')}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <span className="text-xs tabular-nums text-muted-foreground">{total}</span>
      </div>
      {caption && <p className="mt-0.5 text-xs text-muted-foreground">{caption}</p>}

      {total === 0 ? (
        <p className="mt-4 text-xs text-muted-foreground">{empty}</p>
      ) : (
        <>
          <div className="mt-4 flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
            {present.map((s) => (
              <div
                key={s.label}
                className={cn('h-full', s.className)}
                style={{ width: `${(s.value / total) * 100}%` }}
                title={`${s.label}: ${s.value}`}
              />
            ))}
          </div>
          <ul className="mt-3 space-y-1.5">
            {present.map((s) => (
              <li key={s.label} className="flex items-center gap-2 text-xs">
                <span className={cn('h-2 w-2 flex-none rounded-full', s.className)} aria-hidden />
                <span className="flex-1 truncate text-muted-foreground">{s.label}</span>
                <span className="tabular-nums font-medium text-foreground">{s.value}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export interface TrendPoint {
  period: string;
  quantity: number;
  count: number;
}

export interface PositionData {
  trend: TrendPoint[];
  inventory: { optimal: number; low: number; stockout: number; overstock: number } | null;
  shipments: Record<string, number> | null;
  equipment: { byStatus: Record<string, number>; byCondition: Record<string, number> } | null;
  certificates: {
    inDate: number;
    expiringSoon: number;
    expired: number;
    unverified: number;
  } | null;
}

const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

export function PositionBand({ data, loading }: { data: PositionData; loading: boolean }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-52 animate-pulse rounded-xl border bg-muted/40" />
        ))}
      </div>
    );
  }

  const { trend, inventory, shipments, equipment, certificates } = data;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* The only panel with a time axis, because it is the only feed with
            dated history behind it. */}
        <div className={cn(PANEL, 'p-5 lg:col-span-2')}>
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-sm font-semibold text-foreground">Material consumed</h3>
            <span className="text-xs text-muted-foreground">
              {trend.length} {trend.length === 1 ? 'day' : 'days'}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Quantity issued to jobs and wells, by day.
          </p>
          {trend.length === 0 ? (
            <p className="mt-8 text-xs text-muted-foreground">
              No consumption recorded in this period.
            </p>
          ) : (
            <div className="mt-4">
              <ResponsiveContainer width="100%" height={188}>
                {/* No negative left margin. It pulled the axis under the panel edge and
                    clipped the leading digit off every three-figure label — 160
                    rendered as "I60". The YAxis width below is what controls the
                    gutter. */}
                <AreaChart data={trend} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="consumed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  {/* Colours come from the theme tokens, not hex literals — the
                      charts elsewhere in the app are invisible in dark mode. */}
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: string) => v.slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    width={48}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="quantity"
                    name="Quantity"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#consumed)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <SegmentedBar
          title="Stock health"
          caption="Items against their minimum and maximum."
          empty="No inventory levels recorded."
          segments={[
            {
              label: 'Optimal',
              value: inventory?.optimal ?? 0,
              className: 'bg-status-success',
            },
            { label: 'Low', value: inventory?.low ?? 0, className: 'bg-status-warning' },
            {
              label: 'Stockout',
              value: inventory?.stockout ?? 0,
              className: 'bg-status-destructive',
            },
            { label: 'Overstock', value: inventory?.overstock ?? 0, className: 'bg-status-info' },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <SegmentedBar
          title="Equipment"
          caption="Where the fleet is."
          empty="No equipment recorded."
          segments={Object.entries(equipment?.byStatus ?? {}).map(([status, value]) => ({
            label: titleCase(status),
            value,
            className:
              status === 'AVAILABLE'
                ? 'bg-status-success'
                : status === 'IN_USE'
                  ? 'bg-status-info'
                  : status === 'UNDER_MAINTENANCE'
                    ? 'bg-status-warning'
                    : 'bg-status-destructive',
          }))}
        />

        <SegmentedBar
          title="Shipments"
          caption="Everything on file, by state."
          empty="No shipments recorded."
          segments={Object.entries(shipments ?? {}).map(([status, value]) => ({
            label: titleCase(status),
            value,
            className:
              status === 'DELIVERED'
                ? 'bg-status-success'
                : status === 'DELAYED'
                  ? 'bg-status-destructive'
                  : status === 'IN_TRANSIT' || status === 'BOOKED'
                    ? 'bg-status-info'
                    : 'bg-status-neutral',
          }))}
        />

        <SegmentedBar
          title="Certificates"
          caption="Held against stock and assets."
          empty="No certificates on file."
          // The four buckets partition the estate exactly. Deriving "in date"
          // as active-minus-expiring summed to 67 of 68: the one already past
          // its date was counted both as active and as expired, and the two
          // awaiting verification were in neither.
          segments={[
            {
              label: 'In date',
              value: certificates?.inDate ?? 0,
              className: 'bg-status-success',
            },
            {
              label: 'Expiring within 30 days',
              value: certificates?.expiringSoon ?? 0,
              className: 'bg-status-warning',
            },
            {
              label: 'Expired',
              value: certificates?.expired ?? 0,
              className: 'bg-status-destructive',
            },
            {
              label: 'Awaiting verification',
              value: certificates?.unverified ?? 0,
              className: 'bg-status-neutral',
            },
          ]}
        />
      </div>
    </div>
  );
}
